/**
 * VaultMoveDialog — "Move to…" for a folder or a file, the Drive way.
 *
 * Two steps in one modal:
 *   1. pick a destination folder (lazy tree of what the person can see;
 *      system homes and the thing being moved are not offered);
 *   2. see the CONSEQUENCES before anything happens — who gains and loses
 *      access, and which agents keep, lose or gain reach — then confirm.
 *
 * The preview is the backend's move-impact answer, never a guess made here.
 * A drag-and-drop lands in step 2 directly (`presetDest`); a silent move
 * (nobody's access changes) is confirmed by the page without this dialog.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Tree, Spin, message } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { useLanguage } from '@/hooks/useLanguage';
import { vaultService } from '../vaultService';
import type {
  BulkItem, BulkMoveImpact, BulkOutcome, MoveImpact, VaultFolder,
} from '../types';
import { ImpactRow, personLabel, subjectLabel, type ImpactTone } from './VaultImpact';

export interface MoveSource {
  kind: 'folder' | 'file';
  name: string;
  /** The folder being moved, or the file placement's current folder. */
  folderId: string;
  fileId?: string;
  /** A moved folder's current parent — the page refreshes both branches. */
  parentId?: string | null;
}

interface Props {
  source: MoveSource;
  /** 'copy' turns the same picker into "Copy to…": no impact preview (a copy
   *  changes nobody's access — it is a new file), a note saying exactly that,
   *  and the confirm calls copyFile. 'place' is the third gesture (operator
   *  request 2026-08-26): the SAME file shown in another folder — a second
   *  doorway, not a fork. Whoever reaches that folder gets their rights on
   *  the real file, and an edit anywhere changes it everywhere. Both only
   *  for files. */
  mode?: 'move' | 'copy' | 'place';
  /** Set by a drag-drop: skip the picker, open on the impact step. */
  presetDest?: { folderId: string; name: string } | null;
  /** The impact the drag-drop already fetched — shown as-is rather than
   *  asking the server the identical (expensive) question a second time. */
  presetImpact?: MoveImpact | null;
  onMoved: (destFolderId: string) => void;
  onCancel: () => void;
  /** Several selected items at once. The picker then refuses every selected
   *  folder AND everything beneath it (a folder never lands inside itself),
   *  the preview is the merged one for the whole selection, and the confirm
   *  hands the per-item outcome back instead of calling `onMoved`. */
  bulk?: {
    items: BulkItem[];
    onDone: (outcome: BulkOutcome, dest: { folderId: string; name: string }) => void;
  };
}

interface PickNode extends DataNode {
  folder: VaultFolder;
  /** Inside a folder that is itself being moved — never a destination. */
  inside?: boolean;
}

const FolderGlyph = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);

export function VaultMoveDialog({
  source, mode = 'move', presetDest, presetImpact, onMoved, onCancel, bulk,
}: Props) {
  const { t } = useLanguage();
  const copying = mode === 'copy';
  const placing = mode === 'place';
  const [nodes, setNodes] = useState<PickNode[]>([]);
  const [loading, setLoading] = useState(!presetDest);
  const [dest, setDest] = useState<{ folderId: string; name: string } | null>(presetDest || null);
  const [impact, setImpact] = useState<MoveImpact | BulkMoveImpact | null>(presetImpact || null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [moving, setMoving] = useState(false);

  /** A folder that must not be OFFERED: where the thing already is (its own
   *  folder, or the placement's folder — same id either way) and system
   *  homes. The backend refuses both anyway; the picker just does not
   *  dangle them. */
  /* Keyed on the items and plain values, never on the `bulk` / `source`
     objects: the page passes fresh literals on every render, and a picker
     that rebuilt its tree on each of them would lose what the person opened. */
  const bulkItems = bulk?.items;
  const isBulk = !!bulk;
  const movingFolders = useMemo(() => new Set(
    (bulkItems || []).filter((i) => i.kind === 'folder').map((i) => i.folder_id),
  ), [bulkItems]);

  const toNode = useCallback((f: VaultFolder, inside = false): PickNode => {
    // A folder being moved, and everything beneath it, is never a destination.
    const within = inside || (!copying && movingFolders.has(f.folder_id));
    return {
      key: f.folder_id,
      title: f.folder_name,
      folder: f,
      inside: within,
      // Copies of a selection may land right beside the originals ("make a
      // copy" of several, Drive's way), so only a MOVE refuses the folder
      // the items already sit in.
      disabled: within || !!f.system_home
        || (f.folder_id === source.folderId && !(isBulk && copying)),
      icon: <FolderGlyph />,
      isLeaf: false,
    };
  }, [movingFolders, copying, source.folderId, isBulk]);

  /* The picker's tree — not fetched at all when a drag-drop already chose
     the destination and the picker will never render. */
  useEffect(() => {
    if (presetDest) return undefined;
    let dropped = false;
    (async () => {
      try {
        const roots = await vaultService.listRootFolders();
        if (!dropped) setNodes(roots.map((r) => toNode(r)));
      } catch (e: any) {
        message.error(e?.response?.data?.error || t('vault.loadFailed'));
      } finally {
        if (!dropped) setLoading(false);
      }
    })();
    return () => { dropped = true; };
  }, [toNode, t, presetDest]);

  const onLoadData = useCallback(async (node: PickNode) => {
    const folder = await vaultService.getFolder(node.folder.folder_id);
    const kids = (folder.children || [])
      .filter((c) => !c.shared_onto_shelf)
      .map((c) => toNode(c, !!node.inside));
    const patch = (list: PickNode[]): PickNode[] => list.map((n) => (
      n.key === node.key
        ? { ...n, children: kids }
        : n.children ? { ...n, children: patch(n.children as PickNode[]) } : n
    ));
    setNodes((prev) => patch(prev));
  }, [toNode]);

  /* The impact is fetched the moment a destination is known — picked or
     preset — so the confirm step always shows the truth, never a spinner
     behind a button someone already pressed. A copy has no impact to fetch:
     it is a new file that nobody reaches yet. */
  useEffect(() => {
    if (!dest || copying || placing) { setImpact(null); return undefined; }
    // A drag-drop already asked this exact question to decide whether the
    // move was silent; asking again would double the cost of every drop.
    if (presetImpact && dest.folderId === presetDest?.folderId) return undefined;
    let dropped = false;
    setImpactLoading(true);
    (async () => {
      try {
        const res = bulkItems
          ? await vaultService.bulkMoveImpact(bulkItems, dest.folderId)
          : await vaultService.moveImpact({
            folder_id: source.folderId,
            ...(source.kind === 'file' ? { file_id: source.fileId } : {}),
            new_parent_id: dest.folderId,
          });
        if (!dropped) setImpact(res);
      } catch (e: any) {
        if (!dropped) {
          message.error(e?.response?.data?.error || t('vault.actionFailed'));
          setDest(null);
        }
      } finally {
        if (!dropped) setImpactLoading(false);
      }
    })();
    return () => { dropped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dest, source.folderId, source.fileId, source.kind, t, copying, placing,
      presetImpact, presetDest, bulkItems]);

  const doMove = async () => {
    if (!dest) return;
    setMoving(true);
    try {
      if (bulk) {
        const outcome = copying
          ? await vaultService.bulkCopy(bulk.items, dest.folderId,
            t('vault.copy.copyOfName', { name: '{name}' }))
          : await vaultService.bulkMove(bulk.items, dest.folderId);
        bulk.onDone(outcome, dest);
        return;
      }
      if (copying && source.fileId) {
        await vaultService.copyFile(source.folderId, source.fileId, {
          dest_folder_id: dest.folderId,
          new_display_name: t('vault.copy.copyOfName', { name: source.name }),
        });
        message.success(t('vault.copy.done', { name: source.name, dest: dest.name }));
      } else if (placing && source.fileId) {
        await vaultService.placeFile(dest.folderId, source.fileId, source.name);
        message.success(t('vault.place.done', { name: source.name, dest: dest.name }));
      } else if (source.kind === 'file' && source.fileId) {
        await vaultService.moveFile(source.folderId, source.fileId, dest.folderId);
        message.success(t('vault.move.done', { name: source.name, dest: dest.name }));
      } else {
        await vaultService.moveFolder(source.folderId, dest.folderId);
        message.success(t('vault.move.done', { name: source.name, dest: dest.name }));
      }
      onMoved(dest.folderId);
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.actionFailed'));
      setMoving(false);
    }
  };

  /** Every line the preview can show, in the order a person reads them:
   *  what is taken away first, then what is granted, then what is unmoved. */
  const impactRows: { tone: ImpactTone; head: string; items: string[] }[] =
    !impact ? [] : ([
      ['lose', 'peopleLose', impact.losses.map(personLabel)],
      ['gain', 'peopleGain', impact.gains.map(personLabel)],
      ['lose', 'agentsLose', impact.agents.lose.map((s) => subjectLabel(s, t))],
      ['gain', 'agentsGain', impact.agents.gain.map((s) => subjectLabel(s, t))],
      ['keep', 'agentsKeep', impact.agents.keep.map((s) => subjectLabel(s, t))],
    ] as const)
      .filter(([, , items]) => items.length)
      .map(([tone, key, items]) => ({
        tone, items, head: t(`vault.impact.${key}`, { n: items.length }),
      }));

  return (
    <Modal
      open
      title={bulk
        ? t(copying ? 'vault.bulk.copyTitle' : 'vault.bulk.moveTitle', { n: bulk.items.length })
        : placing
        ? t('vault.place.title', { name: source.name })
        : copying
        ? t('vault.copy.title', { name: source.name })
        : t('vault.move.title', { name: source.name })}
      okText={placing ? t('vault.place.confirm')
        : copying ? t('vault.copy.confirm') : t('vault.move.confirm')}
      cancelText={t('vault.cancel')}
      okButtonProps={{ disabled: !dest || impactLoading, loading: moving }}
      onOk={doMove}
      onCancel={onCancel}
      width={520}
    >
      {!presetDest && (
        <div className="vault-move-picker">
          <div className="vault-move-hint">
            {placing ? t('vault.place.pickDest')
              : copying ? t('vault.copy.pickDest') : t('vault.move.pickDest')}
          </div>
          {loading ? (
            <div className="vault-impact-waiting"><Spin /></div>
          ) : (
            <div className="vault-move-tree">
              <Tree
                treeData={nodes}
                loadData={onLoadData as any}
                showIcon
                selectedKeys={dest ? [dest.folderId] : []}
                onSelect={(_, { node }) => {
                  const n = node as unknown as PickNode;
                  if (n.disabled) return;
                  setDest({ folderId: n.folder.folder_id, name: n.folder.folder_name });
                }}
                blockNode
              />
            </div>
          )}
        </div>
      )}

      {dest && (
        <div className="vault-move-impact">
          <div className="vault-move-dest">
            {t('vault.move.destLine', { dest: dest.name })}
          </div>
          {copying && <ImpactRow tone="keep" head={t('vault.copy.note')} />}
          {placing && <ImpactRow tone="gain" head={t('vault.place.note')} />}
          {impact && 'refused' in impact && impact.refused > 0 && (
            <ImpactRow tone="lose" head={t('vault.bulk.refusedInPreview', { n: impact.refused })} />
          )}
          {impactLoading ? (
            <div className="vault-impact-waiting"><Spin size="small" /></div>
          ) : impact && (
            impactRows.length === 0
              ? <ImpactRow tone="keep" head={t('vault.impact.nothingChanges')} />
              : impactRows.map((row) => (
                <ImpactRow key={row.head} tone={row.tone} head={row.head} items={row.items} />
              ))
          )}
        </div>
      )}
    </Modal>
  );
}

export default VaultMoveDialog;
